import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal, Input, Button } from 'common/components';
import { useUpdateMember } from 'services/hooks/mutations';
import { changedProducts } from '../../products';
import { ProductRolesPicker } from '../ProductRolesPicker';
import { PasswordRequirements, passwordOk } from '../PasswordRequirements';
import { memberErrorMessage } from '../memberErrors';
import { EditUserModalProps, EditUserForm } from './EditUserModal.types';

// Edits a person's name, password and roles in the caller's products. Only
// the products whose roles changed are sent; the backend leaves the others —
// including products the caller does not see — as they are. The email is the
// login and cannot change.
//
// Unlike Add and Invite, no role is required: clearing every role in the
// caller's products takes the person out of them, which is right when they
// hold roles in products the caller does not see. When they hold none, the
// backend refuses (400) and DELETE is the way.
export const EditUserModal: React.FC<EditUserModalProps> = ({
    isOpen,
    onClose,
    member,
    onSuccess,
    onError,
    accountId,
    manageable,
}) => {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<EditUserForm>({
        defaultValues: { firstName: '', lastName: '', password: '', products: {} },
    });

    const { updateMember, isPending } = useUpdateMember();
    const password = watch('password');

    useEffect(() => {
        if (member && isOpen) {
            reset({
                firstName: member.first_name,
                lastName: member.last_name,
                password: '',
                products: member.products,
            });
        }
    }, [member, isOpen, reset]);

    const onSubmit = async (data: EditUserForm) => {
        if (!member) return;
        if (data.password && !passwordOk(data.password)) {
            onError('Password does not meet the requirements');
            return;
        }
        try {
            await updateMember({
                accountId,
                id: member.id,
                data: {
                    first_name: data.firstName,
                    last_name: data.lastName,
                    ...(data.password ? { password: data.password } : {}),
                    products: changedProducts(member.products, data.products),
                },
            });
            onClose();
            onSuccess('User updated successfully!');
        } catch (error: any) {
            console.error('Error updating user:', error);
            onError(memberErrorMessage(error, 'Failed to update user. Please try again.'));
        }
    };

    if (!member) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title='Edit User' size='md' showDefaultFooter={false}>
            <form onSubmit={handleSubmit(onSubmit)} id='edit-user-form' className='flex flex-col gap-md'>
                <Input label='Email' type='email' value={member.email} disabled readOnly />

                <Input
                    label='First Name'
                    type='text'
                    {...register('firstName', { required: 'First name is required' })}
                    error={!!errors.firstName}
                    errorText={errors.firstName?.message}
                />

                <Input
                    label='Last Name'
                    type='text'
                    {...register('lastName', { required: 'Last name is required' })}
                    error={!!errors.lastName}
                    errorText={errors.lastName?.message}
                />

                <Input
                    label='Password (leave blank to keep unchanged)'
                    type='password'
                    showPasswordToggle
                    {...register('password')}
                    error={!!errors.password}
                    errorText={errors.password?.message}
                />
                {password && <PasswordRequirements password={password} />}

                <Controller
                    name='products'
                    control={control}
                    render={({ field }) => (
                        <ProductRolesPicker manageable={manageable} value={field.value} onChange={field.onChange} />
                    )}
                />
                <div className='flex justify-end gap-md mt-xl'>
                    <Button type='button' onClick={onClose} variant='secondary'>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={isPending}>
                        Update User
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
